"use client";

import { replaceAppHistoryUrl } from "@/lib/next-history-marker";
import { cn } from "@v1/ui/utils";
import { useTranslations } from "next-intl";
import { type ReactElement, useEffect, useRef, useState } from "react";
import { HELP_ANCHOR_OFFSET_PX, type HelpTocItem } from "./help-headings";

/** Smooth scroll wins over the spy until it lands. */
const JUMP_LOCK_MS = 700;

function prefersReducedMotion(): boolean {
  return (
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

/** Nearest scrolling ancestor; the portal scrolls a panel, `/help` the window. */
function scrollParent(element: HTMLElement): HTMLElement {
  for (let node = element.parentElement; node; node = node.parentElement) {
    const overflowY = window.getComputedStyle(node).overflowY;
    if (overflowY === "auto" || overflowY === "scroll") return node;
  }
  return document.documentElement;
}

function isScrolledToEnd(scroller: HTMLElement): boolean {
  if (scroller === document.documentElement) {
    return (
      window.scrollY > 0 &&
      window.innerHeight + window.scrollY >=
        document.documentElement.scrollHeight - 4
    );
  }
  return (
    scroller.scrollTop > 0 &&
    scroller.clientHeight + scroller.scrollTop >= scroller.scrollHeight - 4
  );
}

function activeItemId(items: readonly HelpTocItem[]): string | null {
  let active = items[0]?.id ?? null;
  for (const item of items) {
    const heading = document.getElementById(item.id);
    if (!heading) continue;
    if (heading.getBoundingClientRect().top > HELP_ANCHOR_OFFSET_PX + 8) break;
    active = item.id;
  }
  const first = items[0] ? document.getElementById(items[0].id) : null;
  if (first && isScrolledToEnd(scrollParent(first))) {
    return items[items.length - 1]?.id ?? active;
  }
  return active;
}

/** "Auf dieser Seite": follows the scroll position, click scrolls to the heading. */
export function HelpToc({
  items,
}: {
  readonly items: readonly HelpTocItem[];
}): ReactElement {
  const t = useTranslations("public.helpToc");
  const [activeId, setActiveId] = useState<string | null>(items[0]?.id ?? null);
  const jumpLock = useRef<number | null>(null);

  useEffect(() => {
    let frame = 0;
    const sync = (): void => {
      frame = 0;
      if (jumpLock.current === null) setActiveId(activeItemId(items));
    };
    const onScroll = (): void => {
      if (frame === 0) frame = window.requestAnimationFrame(sync);
    };
    sync();
    // Capture: panel scrolls do not bubble to window.
    window.addEventListener("scroll", onScroll, {
      passive: true,
      capture: true,
    });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll, { capture: true });
      window.removeEventListener("resize", onScroll);
      if (frame !== 0) window.cancelAnimationFrame(frame);
    };
  }, [items]);

  useEffect(
    () => () => {
      if (jumpLock.current !== null) window.clearTimeout(jumpLock.current);
    },
    [],
  );

  const jumpTo = (id: string): void => {
    const heading = document.getElementById(id);
    if (!heading) return;
    setActiveId(id);
    if (jumpLock.current !== null) window.clearTimeout(jumpLock.current);
    jumpLock.current = window.setTimeout(() => {
      jumpLock.current = null;
    }, JUMP_LOCK_MS);
    // Offset comes from the heading's `scroll-mt`; works for window and panel.
    heading.scrollIntoView({
      block: "start",
      behavior: prefersReducedMotion() ? "auto" : "smooth",
    });
    // history-replace: section anchor only; Back must leave the article, not step through sections.
    replaceAppHistoryUrl(
      `${window.location.pathname}${window.location.search}#${id}`,
    );
  };

  return (
    <nav aria-label={t("aufDieserSeite")}>
      <p className="mb-3 text-[13px] font-semibold text-primary/90">
        {t("aufDieserSeite")}
      </p>
      <ul className="border-l border-border">
        {items.map((item) => {
          const active = item.id === activeId;
          return (
            <li key={item.id}>
              <a
                href={`#${item.id}`}
                aria-current={active ? "location" : undefined}
                onClick={(event) => {
                  event.preventDefault();
                  jumpTo(item.id);
                }}
                className={cn(
                  "-ml-px block border-l-2 py-1.5 leading-5 outline-none transition-colors focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-action-500/30",
                  item.level === 1 ? "pl-3 text-[13px]" : "pl-6 text-[12.5px]",
                  active
                    ? "border-action-500 text-action-700"
                    : "border-transparent text-primary/55 hover:text-primary/85",
                )}
              >
                {item.text}
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
